import React, { useState, useEffect, useRef } from 'react'
import { Button } from 'antd'
import { EditPanIcon } from '../../icons'
import RichText from '../../richText'
import { editClient } from '../../../api/team'
import { isMarketingSiteEnabled } from '../../../utils/constant'
import styles from './AspirationsGoalsSection.module.css'
import ConditionalHideWrap from './ConditionalHidewrap'

const useAutoSave = (initialValue, saveFunction, delay = 2000) => {
  const [value, setValue] = useState(initialValue)
  const [lastSavedValue, setLastSavedValue] = useState(initialValue)
  const [isLoading, setIsLoading] = useState(false)
  const timeoutRef = useRef(null)

  const handleChange = (newValue) => {
    setValue(newValue)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(async () => {
      if (newValue !== lastSavedValue) {
        setIsLoading(true)
        try {
          await saveFunction(newValue)
          setLastSavedValue(newValue)
        } catch (error) {
          console.error('AutoSave failed:', error)
        } finally {
          setIsLoading(false)
        }
      }
    }, delay)
  }

  return [value, handleChange, lastSavedValue, isLoading]
}

export default function AspirationsGoalsSection(props) {
  const {
    person = null,
    loginRole = '',
    view = '',
    hideSectionHandle = () => {},
    hideSection = []
  } = props

const [isEditing, setIsEditing] = useState(false)
const [isEditingLTA, setIsEditingLTA] = useState(false)
const [displayData, setDisplayData] = useState({
  areas_of_focus:
    (person && person.profile && person.profile.areas_of_focus) || '',
  long_term_aspirations:
    (person && person.profile && person.profile.long_term_aspirations) || ''
})

const [areasOfFocus, setAreasOfFocus, lastSavedAreasOfFocus, isSavingAreas] = useAutoSave(
  displayData.areas_of_focus,
  async (newValue) => {
    const formattedValue = (newValue || '')
      .replace(/<ul>/g, '<ul style="padding-left: 15px">')
      .replace(/<ol>/g, '<ol style="padding-left: 15px">')
    await editClient({ profile: { areas_of_focus: formattedValue } }, person.id)
  },
  2000
)

const [aspirations, setAspirations, lastSavedAspirations, isSavingAspirations] = useAutoSave(
  displayData.long_term_aspirations,
  async (newValue) => {
    const formattedValue = (newValue || '')
      .replace(/<ul>/g, '<ul style="padding-left: 15px">')
      .replace(/<ol>/g, '<ol style="padding-left: 15px">')
    await editClient(
      { profile: { long_term_aspirations: formattedValue } },
      person.id
    )
  },
  2000
)

  const isEditable =
    (loginRole === 'Expert' || loginRole === 'InternalAdmin') &&
    view === 'Draft'

  const branded = isMarketingSiteEnabled(person)

  useEffect(() => {
    setDisplayData({
      areas_of_focus:
        (person && person.profile && person.profile.areas_of_focus) || '',
      long_term_aspirations:
        (person && person.profile && person.profile.long_term_aspirations) ||
        ''
    })
  }, [person])

  if (!person || !person.profile) {
    return null
  }

  return (
    <ConditionalHideWrap
      name="Aspirations & Goals"
      title="Aspirations & Goals"
      isSection={true}
      syncStatus={(name, status) => hideSectionHandle(name, status)}
      status={hideSection || []}
      view={view}
      condition={view === 'Draft' || (view === 'Preview' && !hideSection.includes('Aspirations & Goals'))}
    >
      <section className={`${styles.section} ${branded ? styles.brandedSection : ''}`}>
        <h1 className={`${styles.sectionTitle} ${branded ? styles.brandedSectionTitle : ''}`}>
          Aspirations & Goals
        </h1>
        <div className={styles.contentContainer}>
          {/* Aspirations Card */}
          <div className={`${styles.card} ${branded ? styles.brandedCard : ''}`}>
            <h3 className={`${styles.cardTitle} ${branded ? styles.brandedCardTitle : ''}`}>
              Aspirations
              {isEditable && (
<div className={styles.editControls}>
                  {isEditingLTA ? (
                    <>
                      <Button
                        type="primary"
                        ghost
                        onClick={() => {
                          setIsEditingLTA(false)
                          setAspirations(
                            (person &&
                              person.profile &&
                              person.profile.long_term_aspirations) ||
                              ''
                          )
                        }}
                        style={{ marginRight: 8 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        loading={isSavingAspirations}
                        onClick={() => setIsEditingLTA(false)}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <EditPanIcon
                      onClick={() => setIsEditingLTA(true)}
                      className={`${styles.editIcon} ${branded ? styles.brandedEditIcon : ''}`}
                    />
                  )}
                </div>
              )}
            </h3>
<div className={`${styles.cardContent} ${branded ? styles.brandedCardContent : ''}`}>
              {isEditable && isEditingLTA ? (
                <RichText
                  value={aspirations}
                  onChange={setAspirations}
                  fontsizeOnly={true}
                  toolbar={{
                    options: ['fontSize', 'list'],
                    list: {
                      options: ['unordered', 'ordered']
                    }
                  }}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: lastSavedAspirations
                  }}
                />
              )}
            </div>
          </div>

          {/* Goals Card */}
          <div className={`${styles.card} ${branded ? styles.brandedCard : ''}`}>
            <h3 className={`${styles.cardTitle} ${branded ? styles.brandedCardTitle : ''}`}>
              Goals
              {isEditable && (
                <div className={styles.editControls}>
                  {isEditing ? (
                    <>
                      <Button
                        type="primary"
                        ghost
                        onClick={() => {
                          setIsEditing(false)
                          setAreasOfFocus(
                            (person &&
                              person.profile &&
                              person.profile.areas_of_focus) ||
                              ''
                          )
                        }}
                        style={{ marginRight: 8 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        loading={isSavingAreas}
                        onClick={() => setIsEditing(false)}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <EditPanIcon
                      onClick={() => setIsEditing(true)}
                      className={`${styles.editIcon} ${branded ? styles.brandedEditIcon : ''}`}
                    />
                  )}
                </div>
              )}
            </h3>
            <div className={`${styles.cardContent} ${branded ? styles.brandedCardContent : ''}`}>
              {isEditable && isEditing ? (
                <RichText
                  value={areasOfFocus}
                  onChange={setAreasOfFocus}
                  fontsizeOnly={true}
                  toolbar={{
                    options: ['fontSize', 'list'],
                    list: {
                      options: ['unordered', 'ordered']
                    }
                  }}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: lastSavedAreasOfFocus
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </ConditionalHideWrap>
  )
}
